import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { JobRole } from '~/shared/data-contract/job-role';

import type { ApplicationFetchers } from '../../fetchers/create-application-fetchers';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';

const createQueryFn =
    (fetchJobRoles: ApplicationFetchers['fetchJobRoles']) =>
    (jobArchitectureId: string, jobClusterId: string) =>
    async ({ signal }: { signal: AbortSignal }): Promise<readonly JobRole[]> => {
        const result = await fetchJobRoles(jobArchitectureId, jobClusterId, { signal });

        if (result.isOk === true) {
            return result.value;
        }

        throw result.error;
    };

type UseJobRolesOptions = {
    readonly enabled?: boolean;
};

export const useJobRoles = (
    jobArchitectureId: string,
    jobClusterId: string,
    options?: UseJobRolesOptions,
): UseQueryResult<readonly JobRole[], Error> => {
    const enabled = options?.enabled ?? true;
    const { fetchJobRoles } = useApplicationFetchers();

    return useQuery({
        enabled,
        queryFn: createQueryFn(fetchJobRoles)(jobArchitectureId, jobClusterId),
        queryKey: jobArchitectureKeys.rolesByCluster(jobArchitectureId, jobClusterId),
        staleTime: 10_000,
        throwOnError: false,
    });
};

export const useIsJobRoleGenerating = (jobArchitectureId: string, jobClusterId: string, jobRoleId: string): boolean => {
    const { data: jobRoles } = useJobRoles(jobArchitectureId, jobClusterId);

    return (
        jobRoles?.some((jobRole) => jobRole.id === jobRoleId && jobRole.generationInfo?.inProgress === true) ?? false
    );
};
